import { FormControlType, type AbstractAttrDescriptor } from '../base-entity/abstact-attr.descriptor';
import { BaseEntityAttrDescriptor, type Selectable } from '../base-entity/base-entity-attr.descriptor';
import { BaseEntityDescriptor } from '../base-entity/base-entity.descriptor';
import { DATE_STYLES, TIME_STYLES } from '../base-entity/date-format';
import { FlexboxDescriptor, FlexDirection } from '../base-entity/flexboxDescriptor';
import { MULTIPLICITIES } from '../base-entity/multiplicity';
import { toSelectables } from '../base-entity/selectables';
import { MAP_PROVIDERS } from '../base-form/address/address-map';
import { ENTITY_FORM_CONTROL_TYPES, ENTITY_VALUE_KINDS } from '../base-entity-definition/entity-definition';
import { ENTITY_ATTRIBUTE_I18N_SCOPE } from '../i18n/base-entity.i18n';
import { ENTITY_ATTRIBUTE_ENTITY_NAME, ENTITY_DEFINITION_ENTITY_NAME } from './entity-authoring-names';

export { ENTITY_ATTRIBUTE_ENTITY_NAME };

/**
 * An `EntityAttributeDefinition` is identified by `code`, unique within its definition — the contract
 * gives it a read-only uuid nothing addresses it by. Referencing attributes therefore have to set
 * `referenceIdField = 'code'`; see the `attributes` attribute of the `Entity Definition` descriptor.
 */
export const ENTITY_ATTRIBUTE_ID_FIELD = 'code';

function createEntityAttributeAttrDescriptors(entityCodes?: () => Selectable[]): AbstractAttrDescriptor[] {
  const codeAttr = new BaseEntityAttrDescriptor('code', FormControlType.TEXT_BOX, 'Code', undefined, true);
  codeAttr.required = true;
  codeAttr.isHeading = true;
  codeAttr.placeholder = 'Key this attribute is stored under in the payload, e.g. orderNumber';

  const nameAttr = new BaseEntityAttrDescriptor('name', FormControlType.TEXT_BOX, 'Name');
  nameAttr.required = true;

  // region what the value is, and how it is edited
  // Two fields rather than one, because the contract separates them: `valueKind` decides how the payload
  // is validated and how RSQL casts a comparison, `formControlType` only decides which widget renders it.
  // Both are dropdowns over the *contract's* enums — the backend rejects anything else — and both name
  // more members than this frontend has components for; `controlTypeOf` degrades an unrenderable control
  // to a text box rather than taking the screen down.
  const valueKindAttr = new BaseEntityAttrDescriptor('valueKind', FormControlType.DROPDOWN, 'Value Kind', toSelectables(ENTITY_VALUE_KINDS));
  valueKindAttr.required = true;

  const formControlTypeAttr = new BaseEntityAttrDescriptor('formControlType', FormControlType.DROPDOWN, 'Control Type', toSelectables(ENTITY_FORM_CONTROL_TYPES));
  formControlTypeAttr.required = true;
  // endregion

  // The authored field order, and therefore the form's control order and the table's column order — see
  // `descriptorOf`. A number typed into a text box: `NumberComponent` does not exist, and the contract's
  // `integer` needs nothing more than the numeric keyboard the input type brings.
  const displayOrderAttr = new BaseEntityAttrDescriptor('displayOrder', FormControlType.TEXT_BOX, 'Display Order', undefined, undefined, { inputType: 'number' });

  // region how many values
  // Left empty, the attribute holds a single value. `maxOccurs` only applies to the `x` forms; the mapper
  // drops it for the others, which the backend would reject.
  const multiplicityAttr = new BaseEntityAttrDescriptor('multiplicity', FormControlType.DROPDOWN, 'Multiplicity', toSelectables(MULTIPLICITIES));
  multiplicityAttr.autosizeColumn = true;
  const maxOccursAttr = new BaseEntityAttrDescriptor('maxOccurs', FormControlType.TEXT_BOX, 'Max occurs', undefined, undefined, { inputType: 'number' });
  maxOccursAttr.hideInTable = true;
  // endregion

  // region flags
  const requiredAttr = new BaseEntityAttrDescriptor('required', FormControlType.CHECKBOX, 'Required');
  // Worth a column: which attributes are indexed is what decides whether an RSQL range query over this
  // entity is cheap, and reading that off the list beats opening every attribute form.
  const indexedAttr = new BaseEntityAttrDescriptor('indexed', FormControlType.CHECKBOX, 'Indexed');
  // At most one attribute of a definition may set it; the backend is what enforces that, since a form
  // editing one row cannot see its siblings.
  const isLinkToDetailsAttr = new BaseEntityAttrDescriptor('isLinkToDetails', FormControlType.CHECKBOX, 'Titles the record');
  // How the attribute's column sits in the generated list — a layout choice, so not a column of this list.
  const autosizeColumnAttr = new BaseEntityAttrDescriptor('autosizeColumn', FormControlType.CHECKBOX, 'Hugs its list column');
  autosizeColumnAttr.hideInTable = true;
  // The flag columns hold a single word each; letting them hug leaves the width to the code and the name.
  [requiredAttr, indexedAttr, isLinkToDetailsAttr].forEach((flagAttr) => (flagAttr.autosizeColumn = true));
  // endregion

  // region conditional fields
  // Each is meaningful only for some `valueKind` / `formControlType` combinations, and all of them are
  // shown unconditionally: the form builder has no notion of a control whose visibility depends on
  // another control's value, and the backend ignores a field that does not apply to the kind.
  //
  // `enumValues` is a TAGS control because it is the `ENUM` kind's option list — a free set of short
  // strings, which is exactly what a chip list edits.
  const enumValuesAttr = new BaseEntityAttrDescriptor('enumValues', FormControlType.TAGS, 'Enum Values');
  enumValuesAttr.hideInTable = true;

  // A dropdown over the organization's definitions, by code, when the form is given them — a callback re-read
  // on every change detection, so a definition saved a moment ago is already on offer. Without one (a test, a
  // host with no definitions to list) it stays the text box it was; the backend resolves the code on save.
  const linkedEntityTypeAttr = entityCodes
    ? new BaseEntityAttrDescriptor('linkedEntityType', FormControlType.DROPDOWN, 'Linked Entity', entityCodes)
    : new BaseEntityAttrDescriptor('linkedEntityType', FormControlType.TEXT_BOX, 'Linked Entity');
  linkedEntityTypeAttr.placeholder = 'Code of the definition FOREIGN_KEY / EMBEDDED_COMPONENTS points at';
  linkedEntityTypeAttr.hideInTable = true;

  // `defaultValue` is `{}` in the contract — any JSON — but a text box is what a user can type, so a
  // number default round-trips as the string `"5"`. The same narrowing `ADDITIONAL_PROPERTIES` makes, and
  // acceptable for the same reason: every default a seed file has ever carried is a scalar.
  const defaultValueAttr = new BaseEntityAttrDescriptor('defaultValue', FormControlType.TEXT_BOX, 'Default Value');
  defaultValueAttr.hideInTable = true;

  // The fallback icon name or image URL for AVATAR form control when no image is uploaded.
  const defaultAvatarAttr = new BaseEntityAttrDescriptor('defaultAvatar', FormControlType.TEXT_BOX, 'Default Avatar');
  defaultAvatarAttr.placeholder = 'Icon name or image URL for AVATAR fallback, e.g. person, sailing';
  defaultAvatarAttr.hideInTable = true;

  // ADDRESS only — the mapper drops them for any other control. Without a provider the map is OpenStreetMap's.
  const showMapAttr = new BaseEntityAttrDescriptor('showMap', FormControlType.CHECKBOX, 'Shows a map');
  showMapAttr.hideInTable = true;
  const mapProviderAttr = new BaseEntityAttrDescriptor('mapProvider', FormControlType.DROPDOWN, 'Map provider', toSelectables(MAP_PROVIDERS));
  mapProviderAttr.hideInTable = true;
  const defaultCountryAttr = new BaseEntityAttrDescriptor('defaultCountry', FormControlType.TEXT_BOX, 'Default country');
  defaultCountryAttr.placeholder = 'ISO 3166-1 alpha-2 code, e.g. HU';
  defaultCountryAttr.pattern = '^[A-Za-z]{2}$';
  defaultCountryAttr.hideInTable = true;

  // The two halves of `dateFormat`, for DATE / DATE_TIME only — the mapper drops them for any other kind,
  // and `timeStyle` for DATE. Left empty, a style falls back to the value kind's default.
  const dateStyleAttr = new BaseEntityAttrDescriptor('dateStyle', FormControlType.DROPDOWN, 'Date Style', toSelectables(DATE_STYLES));
  dateStyleAttr.hideInTable = true;
  const timeStyleAttr = new BaseEntityAttrDescriptor('timeStyle', FormControlType.DROPDOWN, 'Time Style', toSelectables(TIME_STYLES));
  timeStyleAttr.hideInTable = true;
  // endregion

  const identityRow = new FlexboxDescriptor([codeAttr, nameAttr], FlexDirection.ROW);
  identityRow.style = { 'column-gap': '10px' };
  const kindRow = new FlexboxDescriptor([valueKindAttr, formControlTypeAttr, displayOrderAttr], FlexDirection.ROW);
  kindRow.style = { 'column-gap': '10px' };
  const flagRow = new FlexboxDescriptor([requiredAttr, indexedAttr, isLinkToDetailsAttr, autosizeColumnAttr], FlexDirection.ROW);
  flagRow.style = { 'column-gap': '10px' };
  const multiplicityRow = new FlexboxDescriptor([multiplicityAttr, maxOccursAttr], FlexDirection.ROW);
  multiplicityRow.style = { 'column-gap': '10px' };
  const referenceRow = new FlexboxDescriptor([linkedEntityTypeAttr, defaultValueAttr, defaultAvatarAttr], FlexDirection.ROW);
  referenceRow.style = { 'column-gap': '10px' };

  const formatRow = new FlexboxDescriptor([dateStyleAttr, timeStyleAttr], FlexDirection.ROW);
  formatRow.style = { 'column-gap': '10px' };

  const addressRow = new FlexboxDescriptor([showMapAttr, mapProviderAttr, defaultCountryAttr], FlexDirection.ROW);
  addressRow.style = { 'column-gap': '10px' };

  const flexBoxContainer = new FlexboxDescriptor([identityRow, kindRow, multiplicityRow, flagRow, referenceRow, formatRow, addressRow, enumValuesAttr], FlexDirection.COLUMN);
  flexBoxContainer.style = { 'row-gap': '5px', width: 'fit-content' };
  return [flexBoxContainer];
}

/** @param entityCodes the codes the Linked Entity dropdown offers; see {@link EntityDefinitionOptions.codes} */
export function createEntityAttributeDescriptor(entityCodes?: () => Selectable[]): BaseEntityDescriptor {
  return new BaseEntityDescriptor({
    entityName: ENTITY_ATTRIBUTE_ENTITY_NAME,
    attrDescriptors: createEntityAttributeAttrDescriptors(entityCodes),
    i18nScope: ENTITY_ATTRIBUTE_I18N_SCOPE,
    componentParent: ENTITY_DEFINITION_ENTITY_NAME,
    isEmbedded: true,
  });
}
